import { useSelector } from 'react-redux';

import { isInPopUpBrowser } from '@skippay/core/modules/clientInfo/utils';

import { Amount } from '../@types/amount';
import { SanityLanguage } from '../@types/cms';
import { Country } from '../@types/country';
import { Currency } from '../@types/currency';
import { getUserCountry } from '../modules/user';
import { CURRENCY_CZK, CURRENCY_EUR } from '../utils/amountUtils';

export const DEFAULT_COUNTRY = 'CZ';

function trimTrailingSlash(url: string): string {
	return url.replace(/\/+$/, '');
}

export const getSelfCareUrl = (
	country: Country
): {
	inappBrowserUrl: string;
	selfCareUrl: string;
} => {
	const selfCareUrl =
		(country === DEFAULT_COUNTRY ? process.env.SELF_CARE_URL_CS : process.env.SELF_CARE_URL_SK) ||
		'';

	if (process.env.SELF_CARE_URL_CREDENTIALS && isInPopUpBrowser()) {
		// when SELF_CARE_URL_CREDENTIALS is set, insert credentials to the SELF_CARE_URL, but only when we are in pop-up
		// browser. This is needed for iOS do not remember basic auth.
		const selfCareUrlInstance = new URL(selfCareUrl);
		const [username, password] = process.env.SELF_CARE_URL_CREDENTIALS.split(`:`, 2);
		selfCareUrlInstance.username = username;
		selfCareUrlInstance.password = password;

		return {
			inappBrowserUrl: trimTrailingSlash(selfCareUrlInstance.toString()),
			selfCareUrl,
		};
	}

	if (process.env.SELF_CARE_URL_CREDENTIALS) {
		const [scheme, hostname] = selfCareUrl.split(`://`, 2);
		return {
			inappBrowserUrl: `${scheme}://${process.env.SELF_CARE_URL_CREDENTIALS}@${hostname}`,
			selfCareUrl,
		};
	}

	return {
		inappBrowserUrl: selfCareUrl,
		selfCareUrl,
	};
};

const useUserCountryRelatedInfo = (): {
	country: Country;
	sanityLanguage: SanityLanguage;
	currency: Currency;
	inappBrowserUrl: string;
	selfCareUrl: string;
	deferredPayment: {
		lowLimit: Amount;
	};
	revolving: {
		insufficientIncomeDifference: Amount;
		lowIncomeDifference: Amount;
	};
	installments: {
		defaultMinPrincipal: Amount;
	};
	limitAmendment: {
		insufficientIncomeDifference: Amount;
		lowIncomeDifference: Amount;
	};
	nullAmount: Amount;
	cashbackOptions: Amount[];
} => {
	const country = useSelector(getUserCountry) || DEFAULT_COUNTRY;
	const isCountryCZ = country === DEFAULT_COUNTRY;
	const sanityLanguage = isCountryCZ ? SanityLanguage.csCZ : SanityLanguage.skSK;
	const currency = isCountryCZ ? CURRENCY_CZK : CURRENCY_EUR;

	const { inappBrowserUrl, selfCareUrl } = getSelfCareUrl(country);

	return {
		country,
		sanityLanguage,
		currency,
		inappBrowserUrl,
		selfCareUrl,
		deferredPayment: {
			lowLimit: {
				amount: 500_00,
				currency,
			},
		},
		revolving: {
			insufficientIncomeDifference: {
				amount: isCountryCZ ? 251_00 : 25100,
				currency,
			},
			lowIncomeDifference: {
				amount: isCountryCZ ? 2001_00 : 200100,
				currency,
			},
		},
		limitAmendment: {
			insufficientIncomeDifference: {
				amount: isCountryCZ ? 251_00 : 25100,
				currency,
			},
			lowIncomeDifference: {
				amount: isCountryCZ ? 2001_00 : 200100,
				currency,
			},
		},
		installments: {
			defaultMinPrincipal: {
				amount: 150000,
				currency,
			},
		},
		nullAmount: {
			amount: 0,
			currency,
		},
		cashbackOptions: isCountryCZ
			? [
					{ amount: 10000, currency: CURRENCY_CZK },
					{ amount: 25000, currency: CURRENCY_CZK },
					{ amount: 50000, currency: CURRENCY_CZK },
				]
			: [],
	};
};

export default useUserCountryRelatedInfo;
